"use client";

import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";
import { FeaturedStoryCard } from "./featured-story-card";
import { MobileSlotRenderer, slotKey } from "./mobile-slot-renderer";
import { buildTopStoriesMobileSlots } from "./mobile-story-rhythm";
import { SectionHeader } from "./section-header";
import { TopStoryCompact } from "./top-story-compact";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface TopStoriesSectionProps {
  stories: ExploreStoryCard[];
  exploreBase: string;
  className?: string;
}

export function TopStoriesSection({
  stories,
  exploreBase,
  className,
}: TopStoriesSectionProps) {
  const SIDEBAR_LIMIT = 4;
  const featured = stories[0];
  const sidebarStories = stories.slice(1, 1 + SIDEBAR_LIMIT);
  const extraStories = stories.slice(1 + SIDEBAR_LIMIT);
  const hasSidebarStories = sidebarStories.length > 0;

  if (!featured) {
    return null;
  }

  const mobileSlots = buildTopStoriesMobileSlots({
    stories,
  });
  const shouldOptimizeFeaturedImage = canOptimizeExploreImage({
    src: featured.coverImageUrl,
  });

  return (
    <section className={className}>
      <SectionHeader
        seeAllHref={`${exploreBase}/stories`}
        seeAllLabel="See all →"
        subtitle="this week"
        title="Top stories"
      />

      {/* Mobile: rhythm-based layout */}
      <div className="flex flex-col gap-4 px-1 lg:hidden">
        {mobileSlots.map((slot, idx) => (
          <MobileSlotRenderer
            exploreBase={exploreBase}
            key={slotKey({ slot, index: idx })}
            slot={slot}
          />
        ))}
        <Link
          className="self-center py-2 font-medium text-[0.75rem] text-[var(--explore-text-muted)] uppercase tracking-[0.05em] no-underline transition-colors hover:text-[var(--explore-text-secondary)]"
          href={`${exploreBase}/stories`}
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          See all stories →
        </Link>
      </div>

      {/* Desktop: featured + compact list */}
      <div
        className={`hidden items-stretch gap-6 lg:grid ${
          hasSidebarStories ? "lg:grid-cols-[1.3fr_1fr]" : "lg:grid-cols-1"
        }`}
      >
        <div>
          <Link
            className="flex h-full flex-col overflow-hidden rounded-2xl bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow-lg)]"
            href={`${exploreBase}/stories/${featured.storyId}`}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
          >
            <div className="relative mx-2 mt-2 h-[280px] shrink-0 overflow-hidden rounded-t-xl">
              <Image
                alt={featured.headline}
                className="h-full w-full object-cover"
                fill
                sizes="(max-width: 1024px) 100vw, 55vw"
                src={featured.coverImageUrl}
                unoptimized={!shouldOptimizeFeaturedImage}
              />
              {featured.isNew && (
                <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
                  <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
                  <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
                    New
                  </span>
                </div>
              )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col p-5">
              <h3
                className="mb-2 line-clamp-3 text-pretty font-semibold text-[1.5rem] text-[var(--explore-text-primary)] leading-[1.25]"
                style={{ fontFamily: "var(--explore-card-title-stack)" }}
              >
                {featured.headline}
              </h3>
              <div className="min-h-0 flex-1 overflow-hidden [mask-image:linear-gradient(to_bottom,black_calc(100%-1.3rem),transparent)]">
                <p className="text-[0.95rem] text-[var(--explore-text-secondary)] leading-[1.55]">
                  {featured.summary}
                </p>
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
                {(() => {
                  const matLabel = MATERIAL_TYPE_LABELS[featured.materialType];
                  const badgeClass = STORY_BADGE_STYLES[featured.storyBadge];
                  return matLabel ? (
                    <span
                      className={`inline-flex items-center rounded-sm px-2.5 py-0.5 font-bold text-[0.65rem] uppercase tracking-[0.05em] ${badgeClass}`}
                    >
                      {matLabel}
                    </span>
                  ) : null;
                })()}
                {featured.categoryEmoji && (
                  <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.85rem]">
                    {featured.categoryEmoji}
                  </span>
                )}
                {[...featured.productTags, ...featured.topicTags]
                  .slice(0, 2)
                  .map((tag) => (
                    <span
                      className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2.5 py-1 font-medium text-[0.75rem] text-[var(--explore-tag-text)]"
                      key={tag.slug}
                    >
                      {tag.displayName}
                    </span>
                  ))}
                <span className="text-[0.8rem] text-[var(--explore-text-muted)]">
                  {featured.timeAgo} · {featured.readTime}
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* Compact list */}
        {hasSidebarStories ? (
          <div className="flex h-full flex-col gap-3">
            {sidebarStories.map((story) => (
              <TopStoryCompact
                exploreBase={exploreBase}
                key={story.storyId}
                story={story}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Extra stories — horizontal cards below the grid */}
      {extraStories.length > 0 && (
        <div className="mt-6 hidden flex-col gap-4 lg:flex">
          {extraStories.map((story) => (
            <FeaturedStoryCard
              exploreBase={exploreBase}
              key={story.storyId}
              story={story}
            />
          ))}
        </div>
      )}
    </section>
  );
}
